import React from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { alpha, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { CustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import ProfileViewModal, { ProfileViewModalProps } from 'src/components/ProfileViewModal';
import { useStores } from 'src/models';
import getProfileImg from 'src/utils/getProfileImg';

import NotificationsPopover from '../header/NotificationsPopover';

function NavAccount({ onOpenMyInfoModal }: { onOpenMyInfoModal?: VoidFunction }) {
  const { authStore, departmentStore } = useStores();
  const { user } = authStore;
  const { departments } = departmentStore;
  const deptNm = departments.find((dept) => dept.deptSid === user?.deptSid)?.deptNm || '';
  const jobNm = user?.empJobrespCd?.value || '';

  const handleOpenMyInfoModal = () => {
    if (!authStore.isAuthenticated || !authStore.user) return;
    onOpenMyInfoModal && onOpenMyInfoModal();
  };

  const [profileViewModalInfo, setProfileViewModalInfo] = React.useState<ProfileViewModalProps>({
    open: false,
    profileUrl: getProfileImg(user?.profileImgUrl),
  });

  const handleClickProfile = () => {
    setProfileViewModalInfo({
      open: true,
      profileUrl: getProfileImg(user?.profileImgUrl),
      onClose: () => setProfileViewModalInfo({ ...profileViewModalInfo, open: false }),
    });
  };

  return (
    <StyledRoot>
      {/* 프로필 영역 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5 }}>
        <CustomAvatar
          src={getProfileImg(user?.profileImgUrl)}
          alt={user?.userNm}
          name={user?.userNm}
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: 'primary.lighter',
          }}
          onClick={handleClickProfile}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {user?.userNm}
            </Typography>
            {jobNm && (
              <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                {jobNm}
              </Typography>
            )}
          </Box>
          {deptNm && (
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
              {deptNm}
            </Typography>
          )}
          <Box
            component="a"
            href="https://office.hiworks.com/innerwave.co.kr/mail/webmail"
            target="_blank"
            rel="noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.25,
              color: 'text.disabled',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' },
              transition: 'color 0.2s',
            }}
          >
            <EmailIcon sx={{ width: 12, height: 12 }} />
            <Typography variant="caption" noWrap>
              {user?.loginId}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      {/* 액션 버튼 */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1, py: 0.75 }}>
        <NotificationsPopover />
        <ActionButton size="small" onClick={handleOpenMyInfoModal}>
          <Iconify icon="mdi:user" width={14} />내 정보
        </ActionButton>
        <ActionButton size="small" onClick={() => authStore.logout()}>
          <Iconify icon="material-symbols:logout" width={14} />
          로그아웃
        </ActionButton>
      </Stack>

      <ProfileViewModal {...profileViewModalInfo} />
    </StyledRoot>
  );
}

export default observer(NavAccount);

const StyledRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: 'hidden',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  gap: 4,
  padding: '4px 8px',
  borderRadius: 6,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
}));
